import React, {
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	type ComponentPropsWithoutRef,
} from 'react';

import { di } from 'react-magnetic-di';
import { graphql, usePreloadedQuery, useQueryLoader, type PreloadedQuery } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';
import { StudioWorkspaceAri } from '@atlassian/ari';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';
import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';

import StudioTriggerSolutionBuildSolutionPlanQuery from './__generated__/studioTriggerSolutionBuildSolutionPlanQuery.graphql';
import type { studioTriggerSolutionBuildSolutionPlanQuery } from './__generated__/studioTriggerSolutionBuildSolutionPlanQuery.graphql';
import { SolutionPlanStateUpdateListener } from './controllers';
import { PlanCard } from './plan-card';
import { fetchSolutionPlan } from './services';
import {
	BuildStateValues,
	BuildTypeValues,
	PlanStateValues,
	type Build,
	type BuildState,
	type BuildType,
	type PlanState,
} from './types';
import { getWorkspaceIdFromUrl } from './utils';

const PlanCardRenderer = ({
	planCardProps,
	actionRendererProps: { actions, productKey, cloudId, renderingTouchpoint, aaid },
	shouldTriggerSolutionPlanEvent,
}: {
	planCardProps: Partial<ComponentPropsWithoutRef<typeof PlanCard>>;
	actionRendererProps: ActionRendererFor<typeof ActionKeys.StudioTriggerSolutionPlan>;
	shouldTriggerSolutionPlanEvent?: boolean;
}) => {
	di();

	const firstAction = actions[0];
	const { solutionId, workspaceId } = firstAction.data;

	const { sendAnalyticsEvent } = useAnalytics();

	useEffect(() => {
		if (!shouldTriggerSolutionPlanEvent) return;

		sendAnalyticsEvent({
			eventType: 'track',
			action: 'generated',
			actionSubject: 'solutionPlan',
		});
	}, [sendAnalyticsEvent, shouldTriggerSolutionPlanEvent]);

	return (
		<PlanCard
			isLoading={false}
			name={firstAction.data.title}
			solutionVersion={firstAction.data.solutionVersion}
			builds={[]}
			productKey={productKey}
			cloudId={cloudId}
			aaid={aaid}
			refetchSolutionPlan={() => {}}
			currentBuildNumber={0}
			solutionId={solutionId}
			experienceTracker={null}
			workspaceId={workspaceId}
			renderingTouchpoint={renderingTouchpoint}
			solutionState={'DRAFT'}
			{...planCardProps}
		/>
	);
};

const StudioTriggerSolutionBuildActionInner = ({
	refetch,
	queryRef,
	actionRendererProps,
}: {
	isFetching: boolean;
	refetch: () => void;
	queryRef: PreloadedQuery<studioTriggerSolutionBuildSolutionPlanQuery>;
	actionRendererProps: ActionRendererFor<typeof ActionKeys.StudioTriggerSolutionPlan>;
}) => {
	di();

	const { actions, productKey, cloudId, renderingTouchpoint } = actionRendererProps;

	const firstAction = actions[0];
	const { solutionId, solutionVersion, title, workspaceId } = firstAction.data;

	const { sendAnalyticsEvent } = useAnalytics();

	const experienceTracker = useAIMateExperienceTracker();

	const data = usePreloadedQuery<studioTriggerSolutionBuildSolutionPlanQuery>(
		graphql`
			query studioTriggerSolutionBuildSolutionPlanQuery(
				$workspaceAri: ID!
				$solutionId: String!
				$solutionVersion: Int!
				$skip: Boolean!
				$shouldIncludeAri: Boolean!
			) {
				studio_solutionPlan(
					workspaceId: $workspaceAri
					solutionId: $solutionId
					solutionVersion: $solutionVersion
				) @optIn(to: "StudioSolutionPlan") @skip(if: $skip) {
					name
					spaceId
					solutionVersion
					currentBuildNumber
					conversationId
					solutionVersion
					solutionId
					state
					builds {
						sequence {
							associatedObjectAri @include(if: $shouldIncludeAri)
							name
							number
							dependencies
							buildId
							type
							overview
							state
						}
					}
				}
			}
		`,
		queryRef,
	);

	const solutionPlan = data?.studio_solutionPlan;

	const builds: Build[] = useMemo(() => {
		const isStateValue = (state: string | undefined | null): state is BuildState => {
			return BuildStateValues.includes((state as BuildState) ?? '');
		};

		const isTypeValue = (type: string | undefined | null): type is BuildType => {
			return BuildTypeValues.includes((type as BuildType) ?? '');
		};

		if (fg('sltns-1183-order-builds')) {
			return (
				solutionPlan?.builds?.sequence
					?.filter(Boolean)
					.map((build) => ({
						id: build?.buildId ?? '',
						dependencies:
							build?.dependencies?.filter(
								(dependency): dependency is number => !!(dependency ?? false),
							) ?? [],
						name: build?.name ?? '',
						number: build?.number ?? 0,
						overview: build?.overview ?? '',
						state: isStateValue(build?.state) ? build.state : 'FAILED', // AGG Should return the state, otherwise we force it for FAILED
						type: isTypeValue(build?.type) ? build.type : 'AUTOMATION_RULE', // AGG Should return the type, otherwise we force it for AUTOMATION_RULE
						conversationId: solutionPlan?.conversationId ?? '',
						solutionId: solutionPlan?.solutionId ?? '',
						solutionVersion: `${solutionPlan?.solutionVersion ?? ''}`,
						...(fg('rovo_agent_versioning_enabled') && { ari: build?.associatedObjectAri }),
					}))
					.sort((a, b) => a.number - b.number) || []
			);
		}

		return (
			solutionPlan?.builds?.sequence?.filter(Boolean).map((build) => ({
				id: build?.buildId ?? '',
				dependencies:
					build?.dependencies?.filter(
						(dependency): dependency is number => !!(dependency ?? false),
					) ?? [],
				name: build?.name ?? '',
				number: build?.number ?? 0,
				overview: build?.overview ?? '',
				state: isStateValue(build?.state) ? build.state : 'FAILED', // AGG Should return the state, otherwise we force it for FAILED
				type: isTypeValue(build?.type) ? build.type : 'AUTOMATION_RULE', // AGG Should return the type, otherwise we force it for AUTOMATION_RULE
				conversationId: solutionPlan?.conversationId ?? '',
				solutionId: solutionPlan?.solutionId ?? '',
				solutionVersion: `${solutionPlan?.solutionVersion ?? ''}`,
				...(fg('rovo_agent_versioning_enabled') && { ari: build?.associatedObjectAri }),
			})) || []
		);
	}, [solutionPlan]);

	useEffect(() => {
		experienceTracker.start({
			id: ExperienceName.STUDIO_TRIGGER_SOLUTION_PLAN,
			name: ExperienceName.STUDIO_TRIGGER_SOLUTION_PLAN,
		});

		if (!workspaceId) {
			experienceTracker.fail({
				name: ExperienceName.STUDIO_TRIGGER_SOLUTION_PLAN,
				error: new Error('Missing workspaceId'),
			});
		}
	}, [experienceTracker, workspaceId]);

	useEffect(() => {
		if (solutionPlan) {
			const solutionTypes = [
				...(new Set(
					solutionPlan?.builds?.sequence
						?.map((build) => build?.type)
						.filter((buildType): buildType is BuildType => Boolean(buildType)),
				) ?? []),
			];

			sendAnalyticsEvent({
				eventType: 'track',
				action: 'viewed',
				actionSubject: 'solutionPlan',
				attributes: {
					solutionId,
					solutionVersion: solutionPlan?.solutionVersion,
					solutionTypes,
					buildCount: solutionPlan?.builds?.sequence?.length || 0,
				},
			});
		}
	}, [solutionPlan, solutionId, sendAnalyticsEvent]);

	const solutionState = useMemo(() => {
		const isPlanState = (value?: string | null): value is PlanState =>
			PlanStateValues.includes(value as PlanState);

		return isPlanState(solutionPlan?.state) ? solutionPlan.state : undefined;
	}, [solutionPlan?.state]);

	if (!workspaceId) {
		return null;
	}

	return (
		<>
			<SolutionPlanStateUpdateListener refetch={refetch} />
			<PlanCardRenderer
				planCardProps={{
					name: solutionPlan?.name ?? title,
					solutionVersion: `${solutionPlan?.solutionVersion ?? solutionVersion}`,
					builds: builds,
					productKey: productKey,
					cloudId: cloudId,
					refetchSolutionPlan: refetch,
					currentBuildNumber: solutionPlan?.currentBuildNumber ?? 0,
					solutionId: solutionId,
					experienceTracker,
					spaceId: solutionPlan?.spaceId ?? undefined,
					workspaceId,
					renderingTouchpoint,
					solutionState: solutionState ?? 'DRAFT',
				}}
				actionRendererProps={actionRendererProps}
			/>
		</>
	);
};

const StudioTriggerSolutionBuildActionOld = ({
	actions,
	productKey,
	cloudId,
	aaid,
	isHistoricalMessage,
}: ActionRendererFor<typeof ActionKeys.StudioTriggerSolutionPlan>): React.JSX.Element | null => {
	di();

	const firstAction = actions[0];
	const {
		solutionId,
		solutionVersion,
		title,
		workspaceId: workspaceIdFromActionData,
	} = firstAction.data;

	const workspaceId = getWorkspaceIdFromUrl();
	const { sendAnalyticsEvent } = useAnalytics();

	const experienceTracker = useAIMateExperienceTracker();

	const {
		data: solutionPlan,
		isFetching,
		refetch,
	} = useQuery({
		queryKey: ['fetchSolutionPlan', solutionId, solutionVersion],
		queryFn: () => fetchSolutionPlan(workspaceId ?? '', solutionId, solutionVersion),
		enabled: !!workspaceId && !!solutionId && !!solutionVersion,
	});

	const hasSolutionPlan = !!solutionPlan;

	useEffect(() => {
		/**
		 * This event should only happen once when the data was just streamed.
		 * If the user closes the chat and re-open, the message will still return as no historical
		 * As it was saved in store from the stream. So to avoid duplication, we check if solution plan
		 * is not cached. If it is cached it means the user already saw the solution plan card, and we won't send generated event again.
		 */
		if (isFetching && !hasSolutionPlan && !isHistoricalMessage) {
			sendAnalyticsEvent({
				eventType: 'track',
				action: 'generated',
				actionSubject: 'solutionPlan',
			});
		}
	}, [isFetching, hasSolutionPlan, isHistoricalMessage, sendAnalyticsEvent]);

	useEffect(() => {
		experienceTracker.start({
			id: ExperienceName.STUDIO_TRIGGER_SOLUTION_PLAN,
			name: ExperienceName.STUDIO_TRIGGER_SOLUTION_PLAN,
		});

		if (!workspaceId) {
			experienceTracker.fail({
				name: ExperienceName.STUDIO_TRIGGER_SOLUTION_PLAN,
				error: new Error('Missing workspaceId'),
			});
		}
	}, [experienceTracker, workspaceId]);

	useEffect(() => {
		if (solutionPlan && !isFetching) {
			const solutionTypes = [...new Set(solutionPlan.builds.map((build) => build.type))];

			sendAnalyticsEvent({
				eventType: 'track',
				action: 'viewed',
				actionSubject: 'solutionPlan',
				attributes: {
					solutionId,
					solutionVersion: solutionPlan.solutionVersion,
					solutionTypes,
					buildCount: solutionPlan.builds.length || 0,
				},
			});
		}
	}, [solutionPlan, isFetching, solutionId, sendAnalyticsEvent]);

	if (!workspaceId) {
		return null;
	}

	return (
		<>
			<SolutionPlanStateUpdateListener refetch={refetch} />
			<PlanCard
				isLoading={isFetching}
				name={solutionPlan?.name ?? title}
				solutionVersion={solutionPlan?.solutionVersion ?? solutionVersion}
				builds={solutionPlan?.builds ?? []}
				productKey={productKey}
				cloudId={cloudId}
				aaid={aaid}
				spaceId={solutionPlan?.spaceId}
				refetchSolutionPlan={refetch}
				currentBuildNumber={solutionPlan?.currentBuildNumber ?? 0}
				solutionId={solutionId}
				workspaceId={workspaceIdFromActionData}
				experienceTracker={experienceTracker}
				solutionState={solutionPlan?.state ?? 'DRAFT'}
			/>
		</>
	);
};

const StudioTriggerSolutionBuildActionNew = (
	props: ActionRendererFor<typeof ActionKeys.StudioTriggerSolutionPlan>,
) => {
	const { actions, isHistoricalMessage } = props;

	const [queryRef, loadQuery] = useQueryLoader<studioTriggerSolutionBuildSolutionPlanQuery>(
		StudioTriggerSolutionBuildSolutionPlanQuery,
	);

	const firstAction = actions[0];
	const { solutionId, solutionVersion, workspaceId } = firstAction.data;

	const workspaceAri = useMemo(
		() =>
			StudioWorkspaceAri.create({
				workspaceId,
			}).toString(),
		[workspaceId],
	);

	const getQueryAttributes = useCallback(
		() => ({
			workspaceAri: workspaceAri ?? '',
			solutionId,
			solutionVersion: Number(solutionVersion),
			skip: !workspaceAri,
			shouldIncludeAri: fg('rovo_agent_versioning_enabled'),
		}),
		[solutionId, solutionVersion, workspaceAri],
	);

	useEffect(() => {
		loadQuery(getQueryAttributes());
	}, [getQueryAttributes, loadQuery]);

	const refetch = useCallback(async () => {
		loadQuery(getQueryAttributes(), { fetchPolicy: 'network-only' });
	}, [getQueryAttributes, loadQuery]);

	const getSkeleton = (shouldTriggerSolutionPlanEvent?: boolean) => {
		return (
			<PlanCardRenderer
				planCardProps={{
					isLoading: true,
				}}
				actionRendererProps={props}
				shouldTriggerSolutionPlanEvent={shouldTriggerSolutionPlanEvent}
			/>
		);
	};

	return queryRef ? (
		<Suspense fallback={getSkeleton(!isHistoricalMessage)}>
			<StudioTriggerSolutionBuildActionInner
				actionRendererProps={props}
				isFetching
				queryRef={queryRef}
				refetch={refetch}
			/>
		</Suspense>
	) : (
		getSkeleton()
	);
};

export const StudioTriggerSolutionBuildAction = (
	props: ActionRendererFor<typeof ActionKeys.StudioTriggerSolutionPlan>,
): React.JSX.Element => {
	return fg('fe_enable_sa_externally') ? (
		<StudioTriggerSolutionBuildActionNew {...props} />
	) : (
		<StudioTriggerSolutionBuildActionOld {...props} />
	);
};
